import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useData } from '../../Context/DataContext';
import Badge from '../../components/Badge/Badge';
import styles from './AdminLayout.module.css';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const {
    approvalsCount,
    verificationCount,
    failedPayoutsCount,
    openTicketsCount,
    openSafetyCount
  } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const pathname = location.pathname;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const isRouteActive = (route, subRoutes = []) => {
    if (pathname === route) return true;
    return subRoutes.some((sub) => pathname.startsWith(sub));
  };

  const displayName = (user && typeof user === 'object' && user.name && user.name !== 'info' && user.name !== 'admin')
    ? user.name
    : 'Nishant K.';
  const displayRole = (user && typeof user === 'object' && user.role) ? user.role : 'Super admin';
  const displayInitials = displayName === 'Nishant K.' ? 'NK' : displayName.slice(0, 2).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={styles.appContainer}>
      <div className={styles.mobileHeader}>
        <Link to="/" className={styles.mobileBrand}>
          <div className={styles.mobileLogo}>N</div>
          <span className={styles.mobileTitle}>Nopser Admin</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={styles.mobileToggle}
          aria-label="Toggle navigation"
        >
          ☰
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className={styles.backdrop}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`${styles.sidebar} ${mobileMenuOpen ? styles.sidebarOpen : ''}`.trim()}>
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.brandCard} onClick={() => setMobileMenuOpen(false)}>
            <div className={styles.brandLogo}>N</div>
            <div className={styles.brandText}>
              <div className={styles.brandTitle}>Nopser Admin</div>
              <div className={styles.brandSubtitle}>Operations console</div>
            </div>
          </Link>
          <button
            type="button"
            className={styles.mobileCloseBtn}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <label className={styles.searchLabel}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6C7278" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            placeholder="Search users, studies, tickets"
            aria-label="Search users, studies, tickets"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </label>

        <nav className={styles.navMenu}>
          <div className={styles.navSectionHeading}>MAIN MENU</div>

          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${pathname === '/' || pathname === '/overview' ? styles.navItemActive : ''}`.trim()}
          >
            <span>Overview</span>
          </Link>

          <Link
            to="/approvals"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/approvals', ['/study-review', '/study-approved']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Study approvals</span>
            {approvalsCount > 0 && <Badge variant="solidNeutral">{approvalsCount}</Badge>}
          </Link>

          <Link
            to="/researchers"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/researchers', ['/researcher-profile']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Researchers</span>
          </Link>

          <Link
            to="/participants"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/participants', ['/participant-profile']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Participants</span>
          </Link>

          <Link
            to="/verification"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/verification', ['/doc-review']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Verification</span>
            {verificationCount > 0 && <Badge variant="solidNeutral">{verificationCount}</Badge>}
          </Link>

          <Link
            to="/payments"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/payments', ['/payout-batch', '/failed-payouts']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Payments</span>
            {failedPayoutsCount > 0 && <Badge variant="solidDanger">{failedPayoutsCount}</Badge>}
          </Link>

          <Link
            to="/support"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/support', ['/ticket-detail']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Support</span>
            {openTicketsCount > 0 && <Badge variant="solidNeutral">{openTicketsCount}</Badge>}
          </Link>

          <Link
            to="/safety"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/safety', ['/fraud-case']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Trust & safety</span>
            {openSafetyCount > 0 && <Badge variant="solidDanger">{openSafetyCount}</Badge>}
          </Link>

          <div className={styles.navSectionHeading}>TOOLS</div>

          <Link
            to="/revenue"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/revenue', ['/edit-pricing']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Revenue & plans</span>
          </Link>

          <Link
            to="/notifications"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${isRouteActive('/notifications', ['/send-notification']) ? styles.navItemActive : ''}`.trim()}
          >
            <span>Notifications</span>
          </Link>

          <Link
            to="/team"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${pathname === '/team' ? styles.navItemActive : ''}`.trim()}
          >
            <span>Admin team</span>
          </Link>

          <Link
            to="/settings"
            onClick={() => setMobileMenuOpen(false)}
            className={`${styles.navItem} ${pathname === '/settings' ? styles.navItemActive : ''}`.trim()}
          >
            <span>Settings</span>
          </Link>
        </nav>

        <div className={styles.userCard}>
          <div className={styles.userAvatar}>{displayInitials}</div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{displayName}</span>
            <span className={styles.userRole}>{displayRole}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            Log out
          </button>
        </div>
      </aside>

      <main className={styles.mainContent}>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
